import React, { useState, useEffect } from "react";
import { useBar } from "../../../../context/BarContext";
import { useBarOwnerData } from "../../../../context/BarOwnerDataContext";
import EditBarModal from "./EditBarModal";
import BarDetailModal from "./BarDetailModal";
import defaultBarOutdoor from "../../../../assets/images/default-bar-outdoor.png";

const BarList = ({
  isMapsLoaded = false,
  allowAddBar = true,
  emptyStateRole = "owner",
}) => {
  const [selectedBar, setSelectedBar] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isAddEditModalOpen, setIsAddEditModalOpen] = useState(false);

  const { refreshBars } = useBar();
  const { fetchBars, getBars, invalidateCache } = useBarOwnerData();

  const { bars, loading, hasFetched } = getBars();

  useEffect(() => {
    const cached = getBars();
    if (cached.bars.length > 0) return;
    if (cached.loading) return;
    if (cached.hasFetched) return;
    fetchBars();
  }, [fetchBars, getBars]);

  const handleBarsRefresh = () => {
    invalidateCache("bars");
    fetchBars();
    refreshBars();
  };

  const handleAddBar = () => {
    setIsAddEditModalOpen(true);
  };

  const handleBarClick = (bar) => {
    setSelectedBar(bar);
    setIsDetailModalOpen(true);
  };

  const handleCloseDetailModal = () => {
    setIsDetailModalOpen(false);
    setSelectedBar(null);
  };

  const handleBarDeleted = (barId) => {
    if (selectedBar?.bar_id === barId) {
      handleCloseDetailModal();
    }
  };

  const handleAddEditSuccess = () => {
    handleBarsRefresh();
    setIsAddEditModalOpen(false);
  };

  const handleCloseAddEditModal = () => {
    setIsAddEditModalOpen(false);
  };

  const getBarPhoto = (bar) => {
    let outdoor = bar.outdoor_photos;
    if (typeof outdoor === "string") {
      try {
        outdoor = JSON.parse(outdoor || "{}");
      } catch {
        outdoor = {};
      }
    }
    return outdoor?.photos?.[0] || null;
  };

  return (
    <div className="management-content">
      <div className="management-section">
        {allowAddBar && (
          <div className="management-actions">
            <button className="add-btn" onClick={handleAddBar}>
              + Add New Bar
            </button>
          </div>
        )}

        <div className="bars-grid">
          {loading ? (
            <div
              className="chat-messages-loading"
              style={{
                gridColumn: "1 / -1",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "3rem",
                minHeight: "400px",
                width: "100%",
              }}
            >
              <div className="spinner" aria-hidden="true" />
              <p
                style={{
                  color: "var(--text-secondary)",
                  margin: 0,
                  marginTop: "1rem",
                }}
              >
                Loading bars...
              </p>
            </div>
          ) : (
            bars.map((bar) => {
              const photoUrl = getBarPhoto(bar);
              return (
                <div
                  key={bar.bar_id}
                  className="bar-card"
                  onClick={() => handleBarClick(bar)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleBarClick(bar);
                    }
                  }}
                  aria-label={`View ${bar.name}`}
                >
                  <div className="bar-card-photos">
                    {photoUrl ? (
                      <img
                        src={photoUrl}
                        alt={bar.name}
                        className="bar-card-photo"
                        onError={(e) => {
                          e.currentTarget.src = defaultBarOutdoor;
                        }}
                      />
                    ) : (
                      <div className="bar-card-photo-placeholder">🏪</div>
                    )}
                  </div>
                  <div className="bar-card-content">
                    <h3 className="bar-card-name">{bar.name}</h3>
                  </div>
                </div>
              );
            })
          )}

          {bars.length === 0 && !loading && hasFetched && (
            <div className="no-bars-message">
              <span className="no-bars-icon">🏪</span>
              <h3>
                {emptyStateRole === "admin"
                  ? "No bars found"
                  : "No Bars Yet"}
              </h3>
              <p>
                {emptyStateRole === "admin"
                  ? "When a bar owner invites you as an admin, their venues will appear here."
                  : "Add your first bar to get started"}
              </p>
            </div>
          )}
        </div>

        {allowAddBar && isAddEditModalOpen && (
          <EditBarModal
            isOpen={isAddEditModalOpen}
            onClose={handleCloseAddEditModal}
            bar={null}
            onSuccess={handleAddEditSuccess}
            isMapsLoaded={isMapsLoaded}
          />
        )}

        {selectedBar && (
          <BarDetailModal
            isOpen={isDetailModalOpen}
            onClose={handleCloseDetailModal}
            bar={selectedBar}
            isMapsLoaded={isMapsLoaded}
            onBarUpdated={(updatedBar) => {
              setSelectedBar(updatedBar || selectedBar);
            }}
            onBarDeleted={handleBarDeleted}
            onBarsRefresh={handleBarsRefresh}
          />
        )}
      </div>
    </div>
  );
};

export default BarList;
